import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AddCommentOutlined from "@mui/icons-material/AddCommentOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import GridOnRounded from "@mui/icons-material/GridOnRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import Rotate90DegreesCwRounded from "@mui/icons-material/Rotate90DegreesCwRounded";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";
import ZoomOutRounded from "@mui/icons-material/ZoomOutRounded";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import { isFunction, size } from "lodash";
import isNil from "lodash/isNil";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import ResizeableSidebar from "../../../../components/sidebar/ResizeableSidebar";
import FilePreviewImageTypeIcon from "../../../../components/common/FilePreviewImageTypeIcon";
import { isSpreadsheetFile } from "../../../../components/common/isSpreadsheetFile";
import LoanEnhancedCommentTextBox from "../../../../components/common/Loan/LoanEnhancedCommentTextBox";
import EnhancedCommentItemRenderer from "../../../../_src_shared/comments/EnhancedCommentItemRenderer";
import ObjectInfoTooltip from "../../../../components/data/ObjectInfoTooltip";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useCommentUtilsHook } from "../../../../hooks/utils/useCommentUtilsHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import DownloadLoanFileIconButton from "../../shared/DownloadLoanFileIconButton";
import OpenLoanFileInNewTabButton from "../../shared/OpenLoanFileInNewTabButton";
import CreateConditionFromDocButton from "../../../../components/common/Loan/buttons/CreateConditionFromDocButton";
import UpdateLoanDocumentMetadataForm from "../../../../forms/UpdateLoanDocumentMetadataForm";
import { canSeeOfficeWebView } from "./officeWebViewAccess";
import { PANEL_DEFAULT_WIDTH } from "../../../../components/sidebar/shared/panelChrome";

const MIN_WIDTH = 420;
// Opens at the shared panel width so the preview matches every other
// drilldown panel (panelChrome.PANEL_DEFAULT_WIDTH).
const DEFAULT_WIDTH = PANEL_DEFAULT_WIDTH;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 6;
const ZOOM_STEP = 0.25;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Heavy (xlsx + AG Grid) — only loaded when an Excel file is previewed.
const ExcelPreview = lazy(
  () => import("../../../../components/common/ExcelPreview"),
);

// A `file_display_name` containing a comma/semicolon/quote/CR-LF breaks the
// backend's UNQUOTED Content-Disposition header: the comma is an HTTP
// header-field separator (RFC 7230), so the response arrives with multiple
// Content-Disposition headers and Chrome rejects it at the network layer
// (ERR_RESPONSE_HEADERS_MULTIPLE_CONTENT_DISPOSITION) before any JS runs — so
// it can't be caught/retried as-is. We can't change the header client-side, but
// the header is built server-side FROM `file_display_name`, so rewriting that
// stored value to a safe form (then refetching) makes the file load.
// WORKAROUND until the backend quotes the filename in download_file_by_name.
const CD_BREAKING_CHARS = /[,;"\\\r\n]/;
const sanitizeDisplayName = (name) =>
  (name || "")
    .replace(/[\r\n]+/g, " ")
    .replace(/\s*[,;]\s*/g, " - ")
    .replace(/["\\]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

/**
 * LoanFilePreviewSidebar
 *
 * A self-contained pop-out panel for previewing a loan file. The panel shell
 * (slide-in, drag-to-resize, Esc-to-close, header + close button) is provided by
 * the shared `ResizeableSidebar`; this component supplies the file-specific
 * chrome and viewer:
 *   - image viewer with zoom (buttons + ctrl/⌘-wheel), drag-to-pan, rotate,
 *     and reset
 *   - prev / next navigation across the rows currently shown in the table,
 *     with ←/→ keyboard shortcuts and an "x of n" position indicator
 *   - download, open-in-new-tab, file details, and a comments toggle
 *
 * The backdrop is suppressed (`hideBackdrop`) so the rest of the page stays
 * interactive while the preview is open.
 *
 * Files are fetched the same way the rest of the app does it: `getFileById`
 * returns the raw bytes, which we wrap in a Blob and expose as an object URL
 * (revoked on change/unmount).
 */
const LoanFilePreviewSidebar = ({
  file,
  files,
  open,
  loan,
  containerId,
  onSelectFile,
  onClose,
  // When the previewed file has extracts, `parsedExtract` is its index entry and
  // `onOpenParsed` opens the trifold (owned by the parent so it survives the
  // sidebar closing). Absent → no "Parsed" badge.
  parsedExtract = null,
  onOpenParsed,
  // Which screen edge to dock on ("right" default; "left" when a right-docked
  // drawer is already open so the two sit side by side).
  anchor = "right",
}) => {
  const { loading, getFileById, getFileWebViewUrl, updateLoanDocMetadata } =
    useLoanFilesHook();
  const { getFileAssociatedComments } = useCommentUtilsHook();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const userEmail = useSelector(
    (s) => s.user?.emailAddress || s.user?.email,
  );

  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Keep the raw Blob too: spreadsheets are parsed from the bytes directly
  // (see ExcelPreview) rather than by re-fetching the object URL, which the
  // cleanup below revokes as the user pages between documents.
  const [previewBlob, setPreviewBlob] = useState(null);
  // Which file the loaded bytes belong to. On a file switch there is a render
  // where `file` is already the new doc but `previewUrl` is still the OLD one's
  // blob — e.g. an .xlsx blob handed to the PDF <iframe>, which the browser
  // can't display and so DOWNLOADS. Only render a preview whose key matches.
  const [previewKey, setPreviewKey] = useState(null);
  const requestedKeyRef = useRef(null);
  const [showComments, setShowComments] = useState(false);
  // "Edit details" dialog — the same metadata form as the Documents table's
  // Edit Details (name, group/type, status, validity dates, hidden / export /
  // share flags).
  const [editOpen, setEditOpen] = useState(false);

  // Image transform state
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef(null);

  const content_type = file?.content_type;
  const file_display_name = file?.file_display_name;
  const file_storage_name = file?.file_storage_name;
  const fileId = file?._id;
  const loanId = containerId || file?.dealId || file?.containerName;

  const isImage = content_type?.startsWith("image/");
  const isPdf = content_type === "application/pdf";
  const isExcel = isSpreadsheetFile({
    content_type,
    fileName: file_display_name,
  });

  const relatedComments = getFileAssociatedComments(loan?.comments, fileId);

  // Excel sheets need far more horizontal room (lots of columns), so open the
  // panel wide for spreadsheets AND let the user drag it all the way to full
  // width; every other file type opens at the default width and caps at 1200.
  // Evaluated once when the sidebar mounts (i.e. when first opened) —
  // ResizeableSidebar owns the width state from there on.
  const excelMaxWidth = Math.max(MIN_WIDTH, window.innerWidth - 8);
  const defaultWidth = isExcel
    ? clamp(Math.round(window.innerWidth * 0.7), MIN_WIDTH, excelMaxWidth)
    : DEFAULT_WIDTH;

  // "Open in browser" via the Microsoft Office Online viewer. It needs an
  // anonymously-fetchable URL for the blob (a short-lived Azure SAS from the
  // backend), so it's gated to dev/allowlist until that endpoint ships.
  const canOfficeView = isExcel && canSeeOfficeWebView(userEmail);
  const handleOpenInOfficeWeb = useCallback(() => {
    if (!loanId || !file_storage_name) return;
    // Open the tab synchronously inside the click handler so the browser
    // doesn't treat the later navigation as a blocked popup; point it at the
    // Office viewer once the SAS URL comes back.
    const tab = window.open("about:blank", "_blank");
    getFileWebViewUrl({
      loanId,
      file_storage_name,
      onSuccessFn: (data) => {
        const url = typeof data === "string" ? data : data?.url;
        if (!url) {
          tab?.close?.();
          enqueueSnackbar("In-browser view isn't available for this file.", {
            variant: "warning",
          });
          return;
        }
        const viewer = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
          url,
        )}`;
        if (tab) tab.location.href = viewer;
        else window.open(viewer, "_blank", "noopener,noreferrer");
      },
      onFailFn: () => {
        tab?.close?.();
        enqueueSnackbar(
          "Couldn't open the in-browser viewer. Please try again later.",
          { variant: "error" },
        );
      },
    });
  }, [loanId, file_storage_name, getFileWebViewUrl, enqueueSnackbar]);

  // ── Prev / next across the table's currently displayed rows ──────────
  const navList = useMemo(
    () => (Array.isArray(files) ? files.filter((d) => d?._id) : []),
    [files],
  );
  const currentIndex = useMemo(
    () => navList.findIndex((d) => d._id === fileId),
    [navList, fileId],
  );
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < navList.length - 1;

  const goToOffset = useCallback(
    (delta) => {
      if (!isFunction(onSelectFile) || currentIndex < 0) return;
      const next = navList[currentIndex + delta];
      if (next) onSelectFile(next);
    },
    [navList, currentIndex, onSelectFile],
  );

  // ── Reset transform whenever the file changes ────────────────────────
  const resetView = useCallback(() => {
    setScale(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Tracks the file id we've already auto-sanitized, so a still-failing fetch
  // doesn't loop. `fetchFileRef` lets onFailFn re-invoke the latest fetchFile
  // without putting fetchFile in its own dependency list.
  const sanitizeAttemptedRef = useRef(null);
  const fetchFileRef = useRef(null);

  // ── Fetch the file bytes → object URL ────────────────────────────────
  const fetchFile = useCallback(() => {
    if (!loanId || !file_storage_name) return;
    setError(null);
    const requestKey = file_storage_name;
    requestedKeyRef.current = requestKey;
    getFileById({
      loanId,
      file_storage_name,
      isFile: true,
      onSuccessFn: (responseData) => {
        // A slower response for a file the user already paged away from.
        if (requestedKeyRef.current !== requestKey) return;
        try {
          const blob =
            responseData instanceof Blob
              ? responseData
              : new Blob([responseData], { type: content_type });
          setPreviewBlob(blob);
          setPreviewKey(requestKey);
          setPreviewUrl(URL.createObjectURL(blob));
        } catch (err) {
          console.error("Error creating Blob URL:", err);
          setError(
            "Failed to process the file. The format may be unsupported.",
          );
        }
      },
      onFailFn: (err) => {
        // The fetch can fail because the file's display name has chars that
        // break the server's Content-Disposition header (see note up top).
        // Sanitize the stored name ONCE and retry — the header is rebuilt from
        // it. Keyed by file id so a different file gets a fresh attempt.
        if (
          fileId &&
          loanId &&
          file_display_name &&
          CD_BREAKING_CHARS.test(file_display_name) &&
          sanitizeAttemptedRef.current !== fileId
        ) {
          sanitizeAttemptedRef.current = fileId;
          const cleaned = sanitizeDisplayName(file_display_name);
          if (cleaned && cleaned !== file_display_name) {
            updateLoanDocMetadata({
              loanId,
              data: [{ _id: fileId, file_display_name: cleaned }],
              onSuccessFn: (updatedLoan) => {
                if (updatedLoan) dispatch(loanDrilldownSet(updatedLoan));
                fetchFileRef.current?.();
              },
              onFailFn: () =>
                setError("Failed to load the file. Please try again."),
            });
            return;
          }
        }
        console.error("Error fetching file:", err);
        setError("Failed to load the file. Please try again.");
      },
    });
  }, [
    loanId,
    file_storage_name,
    content_type,
    fileId,
    file_display_name,
    getFileById,
    updateLoanDocMetadata,
    dispatch,
  ]);
  fetchFileRef.current = fetchFile;

  useEffect(() => {
    if (!open || !file || !file_storage_name || !loanId) return;
    resetView();
    setShowComments(false);
    fetchFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileId, file_storage_name, loanId]);

  // Revoke the previous object URL when it changes / on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // ── Keyboard shortcuts (ResizeableSidebar already handles Esc) ────────
  useEffect(() => {
    // Paused while the edit dialog is open — arrows there move a text caret.
    if (!open || editOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goToOffset(-1);
      else if (e.key === "ArrowRight") goToOffset(1);
      else if (isImage && (e.key === "+" || e.key === "=")) zoomBy(ZOOM_STEP);
      else if (isImage && e.key === "-") zoomBy(-ZOOM_STEP);
      else if (isImage && (e.key === "0" || e.key === "r")) resetView();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editOpen, goToOffset, isImage, resetView]);

  // ── Zoom / pan helpers ───────────────────────────────────────────────
  const zoomBy = useCallback((delta) => {
    setScale((s) => {
      const next = clamp(Number((s + delta).toFixed(2)), ZOOM_MIN, ZOOM_MAX);
      if (next <= 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const handleWheel = useCallback(
    (e) => {
      if (!isImage) return;
      // Only hijack the wheel for zoom when modifier is held, so normal
      // scrolling of tall images still works.
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    },
    [isImage, zoomBy],
  );

  const handlePointerDown = (e) => {
    if (!isImage || scale <= 1) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!dragState.current) return;
    setOffset({
      x: dragState.current.originX + (e.clientX - dragState.current.startX),
      y: dragState.current.originY + (e.clientY - dragState.current.startY),
    });
  };
  const endPan = () => {
    dragState.current = null;
  };

  if (!open || isNil(file)) return null;

  const zoomPct = Math.round(scale * 100);
  const canPan = isImage && scale > 1;

  // ── Header breadcrumb (docGroup › docType) + optional "Parsed" badge ──
  const hasBreadcrumb = !!(file?.docGroup || file?.docType);
  const descriptionContent =
    hasBreadcrumb || parsedExtract ? (
      <Flx ac g={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
        {hasBreadcrumb && (
          <Flx ac g={0.25} sx={{ color: "#64748b" }}>
            <Txt sx={{ fontSize: 11, color: "inherit" }}>
              {file?.docGroup || "N/A"}
            </Txt>
            <KeyboardArrowRightRounded sx={{ fontSize: 13 }} />
            <Txt sx={{ fontSize: 11, color: "inherit" }}>
              {file?.docType || "N/A"}
            </Txt>
          </Flx>
        )}
        {parsedExtract && (
          <Tooltip title="View parsed extract (OCR · JSON · images)">
            <Flx
              ac
              g={0.4}
              onClick={() => onOpenParsed?.()}
              sx={{
                px: 0.75,
                py: "1px",
                borderRadius: "10px",
                bgcolor: "#fce7f3",
                color: "#db2777",
                border: "1px solid #fbcfe8",
                cursor: "pointer",
                "&:hover": { bgcolor: "#fbcfe8" },
              }}
            >
              <AutoAwesomeOutlined sx={{ fontSize: "12px !important" }} />
              <Txt sx={{ fontSize: 10, fontWeight: 600, color: "#db2777" }}>
                Parsed
              </Txt>
            </Flx>
          </Tooltip>
        )}
      </Flx>
    ) : null;

  // ── Toolbar — nav / zoom / comments / download / details ─────────────
  const toolbar = (
    <Flx
      ac
      jb
      g={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderTop: "1px solid #e2e8f0",
        borderBottom: "1px solid #e2e8f0",
        background: "#f8fafc",
        flexShrink: 0,
        "& .MuiIconButton-root": { p: 0.6, borderRadius: "8px" },
        "& .MuiSvgIcon-root": { fontSize: "19px !important" },
        "& .MuiBadge-badge": {
          minWidth: 15,
          height: 15,
          fontSize: 9,
          padding: 0,
        },
      }}
    >
      <Flx ac g={0.25}>
        <Tooltip title="Previous file (←)">
          <span>
            <IconButton
              size="small"
              disabled={!hasPrev}
              onClick={() => goToOffset(-1)}
            >
              <ChevronLeftRounded />
            </IconButton>
          </span>
        </Tooltip>
        <Txt
          sx={{
            fontSize: 11,
            color: "#64748b",
            minWidth: 54,
            textAlign: "center",
          }}
        >
          {currentIndex >= 0 ? `${currentIndex + 1} of ${navList.length}` : "—"}
        </Txt>
        <Tooltip title="Next file (→)">
          <span>
            <IconButton
              size="small"
              disabled={!hasNext}
              onClick={() => goToOffset(1)}
            >
              <ChevronRightRounded />
            </IconButton>
          </span>
        </Tooltip>
      </Flx>

      <Flx ac g={0.25}>
        {isImage && (
          <>
            <Tooltip title="Zoom out (−)">
              <span>
                <IconButton
                  size="small"
                  disabled={scale <= ZOOM_MIN}
                  onClick={() => zoomBy(-ZOOM_STEP)}
                >
                  <ZoomOutRounded />
                </IconButton>
              </span>
            </Tooltip>
            <Txt
              sx={{
                fontSize: 11,
                color: "#475569",
                minWidth: 38,
                textAlign: "center",
              }}
            >
              {zoomPct}%
            </Txt>
            <Tooltip title="Zoom in (+)">
              <span>
                <IconButton
                  size="small"
                  disabled={scale >= ZOOM_MAX}
                  onClick={() => zoomBy(ZOOM_STEP)}
                >
                  <ZoomInRounded />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rotate 90°">
              <IconButton
                size="small"
                onClick={() => setRotation((r) => (r + 90) % 360)}
              >
                <Rotate90DegreesCwRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset view (0)">
              <IconButton size="small" onClick={resetView}>
                <RestartAltRounded />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          </>
        )}
        <CreateConditionFromDocButton
          doc={file}
          variant="icon"
          onBeforeFullForm={onClose}
        />
        <Tooltip title={showComments ? "Hide comments" : "Comments"}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setShowComments((s) => !s)}
            sx={{
              background: showComments ? "#e0e7ff" : undefined,
            }}
          >
            <Badge badgeContent={size(relatedComments)} color="primary">
              <AddCommentOutlined />
            </Badge>
          </IconButton>
        </Tooltip>
        {canOfficeView && (
          <Tooltip title="Open in browser (Microsoft Office viewer)">
            <IconButton
              size="small"
              onClick={handleOpenInOfficeWeb}
              sx={{ color: "#166534" }}
            >
              <GridOnRounded />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Edit details">
          <IconButton
            size="small"
            color="primary"
            aria-label="Edit document details"
            disabled={!fileId || !loanId}
            onClick={() => setEditOpen(true)}
          >
            <EditOutlined />
          </IconButton>
        </Tooltip>
        <OpenLoanFileInNewTabButton loanId={loanId} fileObject={file} />
        <DownloadLoanFileIconButton
          loanId={loanId}
          fileObject={file}
          showDownloadButton
        />
        <ObjectInfoTooltip
          title="File Details"
          format={[
            { field: "modifiedDate", type: "date" },
            { field: "uploadDate", type: "date" },
            { field: "reportEffectiveDate", type: "date" },
          ]}
          data={file}
          excludeKeys={[
            "_id",
            "content_type",
            "container_type",
            "dealId",
            "file_storage_name",
            "containerName",
          ]}
        />
      </Flx>
    </Flx>
  );

  return (
    <ResizeableSidebar
      open={open}
      onClose={onClose}
      anchor={anchor}
      // No backdrop — the rest of the page stays interactive while previewing.
      hideBackdrop
      defaultWidth={defaultWidth}
      maxWidth={isExcel ? excelMaxWidth : undefined}
      // The sheet table is too heavy to re-lay out on every drag frame.
      freezeBodyWhileResizing={isExcel}
      title={file_display_name || "File preview"}
      titleIcon={
        <Flx
          center
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: "8px",
            background: "#f1f5f9",
          }}
        >
          <FilePreviewImageTypeIcon contentType={content_type} size={18} />
        </Flx>
      }
      descriptionContent={descriptionContent}
      headerBottomContent={toolbar}
      hideDivider
      bodyPadding={0}
      bodySx={{
        // px: 0,
        // py: 0,
        // p: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "transparent",
      }}
    >
      {/* Body — the file viewer */}
      <Box
        onWheel={handleWheel}
        sx={{
          position: "relative",
          flexGrow: 1,
          minHeight: 0,
          overflow: "auto",
          // p: 0.5,
          background: isImage ? "#1e1e1e" : "#f1f5f9",
        }}
      >
        {loading && (
          <Flx
            center
            column
            g={1.5}
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              zIndex: 3,
            }}
          >
            <CircularProgress sx={{ color: "#fff" }} />
            <Txt sx={{ color: "#fff", fontSize: 13 }}>Loading file…</Txt>
          </Flx>
        )}

        {error && !loading && (
          <Flx
            center
            column
            g={1.5}
            sx={{ position: "absolute", inset: 0, p: 3, textAlign: "center" }}
          >
            <ErrorOutline color="error" sx={{ fontSize: 42 }} />
            <Txt sx={{ color: "error.main" }}>{error}</Txt>
            <Button variant="contained" onClick={fetchFile}>
              Try again
            </Button>
          </Flx>
        )}

        {!loading &&
          !error &&
          previewUrl &&
          previewKey === file_storage_name && (
          <>
            {isImage && (
              <Flx
                center
                sx={{
                  minHeight: "100%",
                  p: 2,
                  cursor: canPan
                    ? dragState.current
                      ? "grabbing"
                      : "grab"
                    : "default",
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endPan}
                onPointerLeave={endPan}
              >
                <Box
                  component="img"
                  src={previewUrl}
                  alt={file_display_name || "Image preview"}
                  draggable={false}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "calc(100vh - 160px)",
                    objectFit: "contain",
                    userSelect: "none",
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
                    transition: dragState.current
                      ? "none"
                      : "transform 120ms ease-out",
                  }}
                />
              </Flx>
            )}

            {isPdf && (
              <Box
                component="iframe"
                src={previewUrl}
                title={file_display_name || "PDF preview"}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            )}

            {isExcel && (
              <Box sx={{ position: "absolute", inset: 0 }}>
                <Suspense
                  fallback={
                    <Flx center sx={{ height: "100%" }}>
                      <CircularProgress />
                    </Flx>
                  }
                >
                  <ExcelPreview
                    blob={previewBlob}
                    fileUrl={previewUrl}
                    fileName={file_display_name}
                    height="100%"
                  />
                </Suspense>
              </Box>
            )}

            {!isImage && !isPdf && !isExcel && (
              <Flx
                center
                column
                g={2}
                sx={{ minHeight: "100%", p: 3, textAlign: "center" }}
              >
                <FilePreviewImageTypeIcon contentType={content_type} />
                <Txt sx={{ color: "#475569" }}>
                  This file type{content_type ? ` (${content_type})` : ""} can't
                  be previewed here.
                </Txt>
                <DownloadLoanFileIconButton
                  loanId={loanId}
                  fileObject={file}
                  showDownloadButton
                />
              </Flx>
            )}
          </>
        )}
      </Box>

      {/* Inline comments panel — docked beneath the preview rather than
          opening a separate modal, so you can read the file while you
          comment. Posting goes through the shared enhanced comment box
          (rich text, @-mentions, attachments). */}
      <Collapse in={showComments} sx={{ flexShrink: 0 }}>
        <Flx
          column
          sx={{
            height: "45vh",
            maxHeight: 480,
            borderTop: "1px solid #e2e8f0",
            background: "#ffffff",
          }}
        >
          <Flx
            ac
            jb
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: "1px solid #eef1f6",
              flexShrink: 0,
            }}
          >
            <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              Comments ({size(relatedComments)})
            </Txt>
            <IconButton size="small" onClick={() => setShowComments(false)}>
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Flx>

          <Box sx={{ p: 1.5, flexShrink: 0 }}>
            <LoanEnhancedCommentTextBox
              loan={loan}
              documentIds={fileId ? [fileId] : []}
            />
          </Box>

          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto" }}>
            <EnhancedCommentItemRenderer
              loan={loan}
              comments={relatedComments}
              padding={1.5}
              gap={2}
            />
          </Box>
        </Flx>
      </Collapse>

      {/* Edit details — theme modal z (13200) already sits above the panel. */}
      {editOpen && file && (
        <Dialog
          open
          onClose={() => setEditOpen(false)}
          maxWidth="sm"
          fullWidth
          // The panel closes on a document-level Esc; keep the dialog's Esc
          // from reaching it so it only closes the dialog.
          onKeyDown={(e) => {
            if (e.key === "Escape") e.stopPropagation();
          }}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, pr: 6 }}>
            <Txt sx={{ fontSize: "15px", fontWeight: 600 }}>
              Edit document details
            </Txt>
            <Txt sx={{ fontSize: "12px", color: "#64748b", display: "block" }}>
              {file_display_name}
            </Txt>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={() => setEditOpen(false)}
                aria-label="Close"
                sx={{ position: "absolute", top: 12, right: 12 }}
              >
                <CloseRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <UpdateLoanDocumentMetadataForm
              file={file}
              loanId={loanId}
              onSuccessFn={(updatedLoan) => {
                // The preview holds a snapshot of the doc — swap in the saved
                // copy so the title / breadcrumb reflect the edit.
                const updated = (updatedLoan?.loanDocuments || []).find(
                  (d) => d?._id === fileId,
                );
                if (updated && isFunction(onSelectFile)) onSelectFile(updated);
                setEditOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </ResizeableSidebar>
  );
};

export default LoanFilePreviewSidebar;
